import { useApi } from '@core/api'
import { getClient } from '@core/api/api-state'
import { ExportJobOutput } from '@core/api/generated'
import {
  getUserRole,
  useIsProBuilderIntegrationEnabled,
  useIsWebhookIntegrationEnabled,
} from '@core/main-state'
import { addSnack } from '@core/snack/snack-state'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { createPlaceholderJob, useExportJobs } from './export-items-state'
import * as queries from './export-queries'

const normalizeExportJob = (job: ExportJobOutput): queries.ExportJobBasic => ({
  id: job.id,
  exportType: job.export_type,
  status: job.status as any, // compatibility with deprecated schema
  createdAt: job.created_at,
  fileName: job.file_name,
  webhookDeliveryAttempts:
    job.webhook_delivery_attempts?.map((attempt) => ({
      webhookConfig: attempt.webhook_config,
    })) ?? [],
})

export const exportItemsKeys = {
  all: ['export'] as const,
  list: ['export', 'list'] as const,
  details: (exportJobId: string) => ['export', 'detail', exportJobId] as const,
  exportItems: ['export', 'exportItems'] as const,
  archiveExportJobs: ['export', 'archiveExportJobs'] as const,
}

const getExportTypes = (isProBuilderEnabled, isWebhookEnabled) => {
  const exportTypes: string[] = []
  if (isProBuilderEnabled) {
    exportTypes.push('ProBuilder')
  }
  if (isWebhookEnabled) {
    exportTypes.push(
      'webhook/csv',
      'webhook/txt',
      'webhook/qtiv2p1',
      'webhook/comptia',
      'webhook/surpass',
      'zip/webhook/csv',
      'zip/webhook/txt',
      'zip/webhook/qtiv2p1',
      'zip/webhook/comptia',
      'zip/webhook/surpass',
    )
  }
  return exportTypes
}

export const useGetLatestExports = () => {
  const api = useApi()
  const isProBuilderEnabled = useIsProBuilderIntegrationEnabled()
  const isWebhookEnabled = useIsWebhookIntegrationEnabled()
  const exportTypes = getExportTypes(isProBuilderEnabled, isWebhookEnabled)
  return useQuery({
    queryKey: exportItemsKeys.list,
    queryFn: async () => {
      const response = await api.listExportJobs({
        query: { export_types: exportTypes },
      })

      if (response.error || !response.data) {
        throw new Error('Failed to fetch export jobs')
      }

      // Handle new API response formats
      const exportJobs = response.data.export_jobs.map(normalizeExportJob)
      return {
        exportJobs,
        agg: { aggregate: { count: response.data.count } },
      }
    },
  })
}

export const getExportDetails = async (exportJobId: string) => {
  const client = getClient()
  const { defaultRole } = getUserRole()

  const { data } = await client.query({
    query: queries.EXPORT_RESULT_QUERY,
    context: { role: defaultRole },
    variables: { exportJobId },
    fetchPolicy: 'network-only',
  })

  return data
}

export const useGetExportDetails = (exportJobId: string | null, enabled = true) => {
  return useQuery({
    queryKey: exportItemsKeys.details(exportJobId!),
    queryFn: () => getExportDetails(exportJobId!),
    enabled: !!exportJobId && enabled,
  })
}

export const useListWebhookDeliveries = (exportJobId: string | null, enabled = true) => {
  const api = useApi()
  return useQuery({
    queryKey: ['webhook-deliveries', exportJobId],
    queryFn: async () => {
      const response = await api.listWebhookDeliveries({
        query: { export_job_id: exportJobId },
      })
      return response.data?.deliveries || []
    },
    enabled: !!exportJobId && enabled,
  })
}

const isInvalidIntegration = (error: any) => {
  return error && 'code' in error && error?.code === 'InvalidIntegration'
}

export const useExportApi = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  const [updateDialog, addExportJob, removeExportJob] = useExportJobs((state) => [
    state.updateDialog,
    state.add,
    state.remove,
  ])

  const exportItems = useMutation({
    mutationFn: async (variables: {
      exportType: string
      itemIds?: string[]
      projectIds?: string[]
      fileName?: string
    }) => {
      const response = await api.exportItems({
        body: {
          export_type: variables.exportType,
          item_ids: variables.itemIds!,
          project_ids: variables.projectIds,
          file_name: variables.fileName,
        },
      })

      if (isInvalidIntegration(response.error)) {
        addSnack({
          message: 'The configured integration is not valid. Contact Finetune Support.',
          severity: 'error',
        })

        throw new Error('Invalid integration')
      }

      const jobId = response.data?.job_id

      if (!jobId) {
        console.error('no job id returned')
        throw new Error('no job id returned')
      }

      return jobId
    },
    mutationKey: exportItemsKeys.exportItems,
    async onSuccess(jobId, variables) {
      addExportJob(createPlaceholderJob(jobId, variables.exportType))
      queryClient.prefetchQuery({
        queryKey: exportItemsKeys.list,
      })
    },
  })

  const continueExport = useMutation({
    mutationKey: ['export', 'continueExport'] as const,
    mutationFn: async (variables: {
      exportJob: queries.ExportJobBasic
      mode: queries.ExportContinueMode
    }) => {
      const response = await api.continueExportJob({
        path: { export_id: variables.exportJob.id },
        query: { mode: variables.mode },
      })
      const jobId = response.data?.jobId
      if (!jobId) {
        throw new Error('No job id returned')
      }
      return jobId
    },
    onError(error, variables) {
      console.error(error, variables)
      addSnack({
        message: 'Error while continuing export job',
        severity: 'error',
      })
    },
    async onSuccess(jobId, variables) {
      await queryClient.refetchQueries({
        queryKey: exportItemsKeys.list,
      })

      // remove the export job from the status list
      removeExportJob(variables.exportJob.id)

      if (variables.mode === 'cancel') {
        // do not select the next job
        updateDialog({ selectedJobId: '' })

        // small message just to be clear that something happened
        addSnack({
          message: 'Export job cancelled',
          severity: 'info',
        })
      } else {
        updateDialog({ selectedJobId: jobId })
        addExportJob(createPlaceholderJob(jobId, variables.exportJob.exportType))
      }
    },
  })

  return {
    exportItems,
    continueExport,
  }
}

export const archiveExportJobs = async (ids: Set<string>) => {
  const client = getClient()
  const { defaultRole } = getUserRole()

  const { data } = await client.mutate({
    mutation: queries.ARCHIVE_EXPORT_JOBS,
    context: { role: defaultRole },
    variables: { ids: Array.from(ids) },
  })

  return data!
}
